import { useEffect, useRef, useState } from 'react';

export const RestaurantSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Détection mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Sur mobile, animation plus simple et plus rapide
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: isMobile ? 0.1 : 0.2,
        rootMargin: isMobile ? '0px 0px -20px 0px' : '0px 0px -50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isMobile]);

  const restaurantSolutions = [
    {
      icon: "ri-store-3-line",
      title: "Création de marques virtuelles optimisées",
      description: "Création de concepts de restaurants virtuels adaptés à votre cuisine"
    },
    {
      icon: "ri-palette-line",
      title: "Gestion du branding et des visuels",
      description: "Design professionnel et identité visuelle percutante"
    },
    {
      icon: "ri-megaphone-line",
      title: "Marketing ciblé pour booster les ventes",
      description: "Stratégies marketing pour booster vos ventes"
    },
    {
      icon: "ri-smartphone-line",
      title: "Intégration sur Uber Eats et Deliveroo",
      description: "Présence optimisée sur Uber Eats et Deliveroo"
    },
    {
      icon: "ri-money-euro-circle-line",
      title: "Aucun coût",
      description: "Solution 100% gratuite, aucun investissement requis"
    },
    {
      icon: "ri-shield-check-line",
      title: "Aucun risque",
      description: "Garantie de sécurité totale pour votre restaurant"
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-indigo-900 via-blue-800 to-purple-600 relative overflow-hidden"
    >
      {/* Background Pattern - Opacité réduite sur mobile */}
      <div className={`absolute inset-0 ${isMobile ? 'opacity-15' : 'opacity-10'}`}>
        <div 
          className="w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/9c2d6b83e03b5518affeb7a40f607a37.jpg')`,
            backgroundPosition: isMobile ? 'center 40%' : 'center center',
            backgroundSize: 'cover',
            backgroundAttachment: isMobile ? 'scroll' : 'fixed'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black mb-4 sm:mb-6 transition-all duration-${isMobile ? '500' : '1000'} ${
            isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4'
          }`}>
            <span className="bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
              "BOOST YOUR RESTAURANT"
            </span>
          </h2>
          <p className={`text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto font-accent transition-all duration-${isMobile ? '500' : '1000'} ${isMobile ? 'delay-100' : 'delay-300'} ${
            isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4'
          }`}>
            Spécialement pour les restaurateurs : créons des marques virtuelles qui multiplient vos revenus !
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {restaurantSolutions.map((solution, index) => (
            <div
              key={index}
              className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/20 transition-all duration-${isMobile ? '300' : '500'} group text-center ${
                isVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-6'
              }`}
              style={{
                transitionDelay: isMobile ? `${200 + index * 50}ms` : `${600 + index * 150}ms`
              }}
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300 mx-auto">
                <i className={`${solution.icon} text-xl sm:text-2xl text-white`}></i>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-bold text-white mb-3 sm:mb-4">
                {solution.title}
              </h3>
              <p className="text-sm sm:text-base text-blue-100 leading-relaxed font-primary">
                {solution.description}
              </p>
            </div>
          ))}
        </div>

        {/* Process Flow for Restaurants */}
        <div className={`bg-white/10 backdrop-blur-sm rounded-3xl p-6 sm:p-8 md:p-12 border border-white/20 transition-all duration-${isMobile ? '500' : '1000'} ${isMobile ? 'delay-300' : 'delay-1000'} ${
          isVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-4'
        }`}>
          <h3 className={`text-xl sm:text-2xl md:text-3xl font-heading font-bold text-white text-center mb-8 sm:mb-12 transition-all duration-${isMobile ? '500' : '1000'} ${isMobile ? 'delay-400' : 'delay-1200'} ${
            isVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4'
          }`}>
            Comment ça marche pour votre restaurant ?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { step: "1", title: "Analyse", desc: "Nous analysons votre restaurant et votre menu" },
              { step: "2", title: "Création", desc: "Nous créons vos marques virtuelles optimisées" },
              { step: "3", title: "Lancement", desc: "Mise en ligne sur les plateformes de livraison" },
              { step: "4", title: "Résultats", desc: "Vous récoltez les bénéfices sans rien faire" }
            ].map((item, index) => (
              <div 
                key={index} 
                className={`text-center transition-all duration-${isMobile ? '400' : '700'} ${
                  isVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4'
                }`}
                style={{
                  transitionDelay: isMobile ? `${500 + index * 100}ms` : `${1400 + index * 200}ms`
                }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 text-white font-heading font-bold text-lg sm:text-xl">
                  {item.step}
                </div>
                <h4 className="text-base sm:text-lg font-heading font-bold text-white mb-2">{item.title}</h4>
                <p className="text-blue-100 text-sm font-primary">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
